// Serviço do domínio equipe (membros e convites do workspace).
import { createClient } from '@/lib/supabase/client'
import type { PapelMembro } from '@/lib/types'
import { registrarAuditoria } from '@/services/identity'

export interface MembroComPerfil {
  id: string
  user_id: string | null
  papel: PapelMembro
  ativo: boolean
  convite_email: string | null
  convite_status: 'pendente' | 'aceito' | 'revogado'
  clinica_scope_id: string | null
  perfil: { nome: string; email: string } | null
}

export async function getMembros(workspaceId: string): Promise<MembroComPerfil[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('workspace_membros')
    .select('id, user_id, papel, ativo, convite_email, convite_status, clinica_scope_id, perfis_usuario(nome, email)')
    .eq('workspace_id', workspaceId)
    .neq('convite_status', 'revogado')
    .order('created_at', { ascending: true })
  if (error) throw error

  return (data ?? []).map(row => {
    const raw = (row as Record<string, unknown>).perfis_usuario
    const perfil = (Array.isArray(raw) ? raw[0] : raw) as { nome: string; email: string } | null
    const { perfis_usuario: _drop, ...resto } = row as Record<string, unknown>
    void _drop
    return { ...(resto as unknown as Omit<MembroComPerfil, 'perfil'>), perfil: perfil ?? null }
  })
}

function gerarToken(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

export async function criarConvite(params: {
  workspaceId: string
  email: string
  papel: Exclude<PapelMembro, 'medico_titular'>
  clinicaScopeId?: string | null
}): Promise<{ token: string }> {
  const supabase = createClient()
  const token = gerarToken()
  const { error } = await supabase.from('workspace_membros').insert({
    workspace_id: params.workspaceId,
    user_id: null,
    papel: params.papel,
    clinica_scope_id: params.clinicaScopeId ?? null,
    convite_email: params.email.toLowerCase().trim(),
    convite_token: token,
    convite_status: 'pendente',
    ativo: true,
  })
  if (error) throw error
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'convite_criado',
    entidade: 'workspace_membros',
    dados: { papel: params.papel },
  })
  return { token }
}

export async function revogarConvite(workspaceId: string, membroId: string) {
  const supabase = createClient()
  const { error } = await supabase
    .from('workspace_membros')
    .update({ convite_status: 'revogado', ativo: false })
    .eq('id', membroId)
    .eq('workspace_id', workspaceId)
  if (error) throw error
  await registrarAuditoria({
    workspaceId,
    acao: 'convite_revogado',
    entidade: 'workspace_membros',
    registroId: membroId,
  })
}

export async function desativarMembro(workspaceId: string, membroId: string) {
  const supabase = createClient()
  const { error } = await supabase
    .from('workspace_membros')
    .update({ ativo: false })
    .eq('id', membroId)
    .eq('workspace_id', workspaceId)
  if (error) throw error
  await registrarAuditoria({
    workspaceId,
    acao: 'membro_desativado',
    entidade: 'workspace_membros',
    registroId: membroId,
  })
}
